import { GaliaMarker } from '@features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@features/components/visualization/mapbox/models/galia-route';
import { TimelineRow } from '@features/components/visualization/timeline/models/timeline';

/** What the pointer is over. Never reaches the store. */
export type HoverTarget =
  | { kind: 'route'; routeUid: string }
  | { kind: 'action'; actionUid: string };

export type FocusKind = 'route' | 'action' | 'job';

export interface ResolvedFocus {
  kind: FocusKind;
  /**
   * Route lines and timeline row legends only. Says nothing about those routes' actions:
   * a job pin lights three lines while all but one stop on each stays dim.
   */
  litRouteUids: ReadonlySet<string>;
  /** Actions only. Independent of `litRouteUids`. */
  litActionUids: ReadonlySet<string>;
  /**
   * Actions related to the focused one through a shared job, kept legible as context while
   * the focused action itself stays untouched. Empty for a route focus and for a pin.
   */
  softActionUids: ReadonlySet<string>;
  /**
   * What every route outside `litRouteUids` gets. A focused *route* makes the others noise;
   * a focused *action* leaves the surrounding network as faint context.
   */
  otherRoutes: 'dim' | 'soft';
}

export type RouteFocusState = 'lit' | 'soft' | 'dim' | 'none';
export type ActionFocusState = 'lit' | 'soft' | 'dim' | 'none';

export interface FocusIndex {
  routeOfAction: ReadonlyMap<string, string>;
  actionsOfRoute: ReadonlyMap<string, readonly string[]>;
  actionsOfJob: ReadonlyMap<string, readonly string[]>;
  jobsOfAction: ReadonlyMap<string, readonly string[]>;
  /**
   * For a line that belongs to an entity rather than being one — a job's arcs — the entity it
   * belongs to, keyed by the line's own uid. Empty for routes, whose lines *are* the entity.
   */
  aggregatorOfRoute: ReadonlyMap<string, string>;
}

function push(map: Map<string, string[]>, key: string, value: string): void {
  const existing = map.get(key);
  if (existing) existing.push(value);
  else map.set(key, [value]);
}

/**
 * Builds both indexes in one pass over whatever the two panels are showing.
 *
 * Read from markers *and* rows because either panel can be closed, and deduped on the action
 * uid because the same action is named by one marker and by one task per step.
 *
 * The lines come in as well, for the arcs that belong to an entity instead of being one. A job
 * mints a uid per arc — `<job>_actions`, `<job>_option_0` — while its pickup and delivery
 * markers name the job itself, so the actions of such an arc can only be found through the
 * entity it names. Left unresolved, a focus on the arc lights the line with an empty lit set,
 * which is what used to turn the job's own operations into grey dots.
 */
export function buildFocusIndex(
  markers: readonly GaliaMarker[],
  rows: readonly TimelineRow[],
  routes: readonly GaliaRoute[] = []
): FocusIndex {
  const routeOfAction = new Map<string, string>();
  const actionsOfRoute = new Map<string, string[]>();
  const actionsOfJob = new Map<string, string[]>();
  const jobsOfAction = new Map<string, readonly string[]>();
  const aggregatorOfRoute = new Map<string, string>();

  const add = (
    routeUid: string | undefined,
    actionUid: string | undefined,
    jobUids: readonly string[] | undefined
  ): void => {
    if (!routeUid || !actionUid || routeOfAction.has(actionUid)) return;

    routeOfAction.set(actionUid, routeUid);
    push(actionsOfRoute, routeUid, actionUid);

    const jobs = jobUids ?? [];
    jobsOfAction.set(actionUid, jobs);
    for (const job of jobs) push(actionsOfJob, job, actionUid);
  };

  for (const marker of markers) {
    add(marker.structuralAggregatorIds?.[0], marker.uid, marker.jobUids);
  }
  for (const row of rows) {
    for (const task of row.tasks) add(row.uid, task.actionUid, task.jobUids);
  }

  // After the two passes above, so the entity's actions are already indexed by the time an arc
  // asks for them.
  for (const route of routes) {
    const aggregator = route.aggregatorUid;
    if (!aggregator || aggregator === route.uid) continue;

    aggregatorOfRoute.set(route.uid, aggregator);
    const actions = actionsOfRoute.get(aggregator);
    if (actions) actionsOfRoute.set(route.uid, actions);
  }

  return { routeOfAction, actionsOfRoute, actionsOfJob, jobsOfAction, aggregatorOfRoute };
}

export function sameHoverTarget(a: HoverTarget | null, b: HoverTarget | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.kind === 'route' && b.kind === 'route') return a.routeUid === b.routeUid;
  if (a.kind === 'action' && b.kind === 'action') return a.actionUid === b.actionUid;
  return false;
}
